import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  CheckBadgeIcon,
  CodeBracketIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { Project } from "../../firebase/projectService";
import { PLATFORM_META, normalizeProjectLinks } from "../../utils/projectLinks";
import { getMediaType, getYouTubeVideoId, getYouTubeEmbedUrl } from "../../utils/mediaUtils";

interface ProjectDetailModalProps {
  project: Project | null;
  onClose: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  company: "Company Project",
  freelance: "Freelance Project",
  "open-source": "Open Source",
  personal: "Personal Project",
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="mb-3 text-base font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
    {children}
  </h4>
);

/**
 * Accessible, structured project detail dialog.
 * Sections: Overview · My Role · Key Features · Technologies · Gallery.
 */
const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, onClose }) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Lock body scroll, focus the close button, and wire Esc-to-close.
  useEffect(() => {
    if (!project) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label={`${project.title} details`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Scrim */}
          <div
            className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          <div className="flex min-h-screen items-end justify-center p-0 sm:items-center sm:p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 24 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-4xl rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header bar */}
              <div className="sticky top-0 z-10 flex items-start justify-between gap-4 rounded-t-2xl border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur px-6 py-4">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700">
                    <img src={project.image} alt={project.title} className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                        {project.title}
                      </h3>
                      {project.featured && <StarSolidIcon className="h-5 w-5 text-yellow-400" />}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="chip-neutral">
                        {TYPE_LABELS[project.projectType || "personal"]}
                      </span>
                      {project.company && (
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {project.projectType === "freelance" ? "Client: " : "@ "}
                          {project.company}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={onClose}
                  aria-label="Close project details"
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Body */}
              <div className="max-h-[75vh] overflow-y-auto px-6 py-6">
                {/* My role — prominent, professional callout */}
                {project.myRole && (
                  <div className="mb-6 flex items-center gap-4 overflow-hidden rounded-2xl border border-primary-200/70 dark:border-primary-700/50 bg-gradient-to-br from-primary-50 via-primary-50/40 to-transparent dark:from-primary-900/40 dark:via-primary-900/20 dark:to-transparent px-5 py-4 shadow-sm ring-1 ring-inset ring-white/40 dark:ring-white/5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30">
                      <CheckBadgeIcon className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="mb-0.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-primary-500 dark:text-primary-400">
                        My Role
                      </span>
                      <span className="block text-lg font-bold leading-snug text-gray-900 dark:text-white">
                        {project.myRole}
                      </span>
                    </div>
                  </div>
                )}

                {/* Quick action links */}
                <div className="mb-8 flex flex-wrap gap-3">
                  {normalizeProjectLinks(project).map((link) => {
                    const meta = PLATFORM_META[link.type];
                    const Icon = meta.Icon;
                    return (
                      <a
                        key={link.type}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: meta.color }}
                        className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                      >
                        <Icon className="h-4 w-4" />
                        {meta.ctaLabel}
                      </a>
                    );
                  })}
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline inline-flex items-center gap-2"
                    >
                      <CodeBracketIcon className="h-4 w-4" />
                      Source Code
                    </a>
                  )}
                  {project.companyWebsite && (
                    <a
                      href={project.companyWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline inline-flex items-center gap-2"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      Company
                    </a>
                  )}
                </div>

                {/* Overview */}
                <section className="mb-8">
                  <SectionTitle>Overview</SectionTitle>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    {project.longDescription || project.description}
                  </p>
                </section>

                {/* My contributions */}
                {project.contributions && project.contributions.length > 0 && (
                  <section className="mb-8">
                    <SectionTitle>My Role &amp; Contributions</SectionTitle>
                    <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                      {project.contributions.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckBadgeIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                          <span className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Key features */}
                {project.features && project.features.length > 0 && (
                  <section className="mb-8">
                    <SectionTitle>Key Features</SectionTitle>
                    <div className="grid gap-5 sm:grid-cols-2">
                      {project.features.map((feature, fi) => (
                        <div key={fi} className="rounded-xl border-l-4 border-primary-500 bg-gray-50 dark:bg-gray-900/40 p-4">
                          <h5 className="mb-2 font-semibold text-primary-600 dark:text-primary-400">
                            {feature.name}
                          </h5>
                          <ul className="space-y-1">
                            {feature.subFeatures.map((sub, si) => (
                              <li key={si} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary-400" />
                                {sub}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Technologies */}
                {project.technologies && project.technologies.length > 0 && (
                  <section className="mb-8">
                    <SectionTitle>Technologies</SectionTitle>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, i) => (
                        <span key={i} className="chip">{tech}</span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Gallery */}
                {project.gallery && project.gallery.length > 0 && (
                  <section>
                    <SectionTitle>Gallery</SectionTitle>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {project.gallery.map((item, index) => {
                        const isYouTube = getMediaType(item) === "youtube";
                        const videoId = isYouTube ? getYouTubeVideoId(item) : null;
                        return isYouTube && videoId ? (
                          <div key={index} className="overflow-hidden rounded-xl bg-black shadow-md" style={{ aspectRatio: "16/9" }}>
                            <iframe
                              src={getYouTubeEmbedUrl(videoId)}
                              title={`${project.title} video ${index + 1}`}
                              className="h-full w-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <a
                            key={index}
                            href={item}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700"
                          >
                            <img
                              src={item}
                              alt={`${project.title} screenshot ${index + 1}`}
                              loading="lazy"
                              className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </a>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectDetailModal;
